import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import IGibier from "../../models/IGibier";
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import useEnum from '../../hooks/useEnum';
import './gestion-gibiers.css';

export default function GestionGibiers() {
  const gibierFormDataSource = useCustomDataSource<IGibier, IGibier>({ path: "gibier", primaryKey: "idGibier" });
  const { setMainForm, selectedDataRow: selectedGibier, mode, onFieldDataChanged, setMode, isFieldModifier, setIsFieldModifier } = gibierFormDataSource;
  const formRef = useRef<FormRef>(null);
  const { editorOptions: typesGibierEditorOptions } = useEnum(undefined, "gestionGibierTypes", '25%');
  const { editorOptions: typePetitGibierEditorOptions } = useEnum(undefined, "gestionGibierTypePetitGibier", '25%');
  const { editorOptions: typeGrosGibierEditorOptions } = useEnum(undefined, "gestionGibierTypeGrosGibier", '25%');
  const [renderedTypeDeGibier, setRenderedTypeDeGibier] = useState<string>(selectedGibier?.typeDeGibier || "");

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("nom")?.focus();
  }, [mode])

  useEffect(() => {
    setRenderedTypeDeGibier(selectedGibier?.typeDeGibier || "");
  }, [selectedGibier?.idGibier, selectedGibier?.typeDeGibier]);

  const nomOptions = useMemo(() => { return { maxLength: 50, width: 225 }; }, []);
  const actifOptions = useMemo(() => { return { text: "Actif", }; }, []);
  const nomAnglaisOptions = useMemo(() => { return { maxLength: 50, width: 225 }; }, []);
  const formRenderKey = useMemo(() => `${selectedGibier?.idGibier || "nouveau"}-${renderedTypeDeGibier || "aucun"}`, [selectedGibier?.idGibier, renderedTypeDeGibier]);
  const onGibierFieldDataChanged = useCallback((e: any) => {
    onFieldDataChanged(e);

    if (e.dataField === "typeDeGibier") {
      setRenderedTypeDeGibier(e.value || "");
      formRef.current?.instance().updateData("noGibier", "");
    }
  }, [onFieldDataChanged]);

  const gibierNoOptions = useMemo(() => {
    if (renderedTypeDeGibier === 'Petit') return typePetitGibierEditorOptions;
    if (renderedTypeDeGibier === 'Gros') return typeGrosGibierEditorOptions;
    return undefined;
  }, [renderedTypeDeGibier, typePetitGibierEditorOptions, typeGrosGibierEditorOptions]);

  return (
    <FormListBase data={gibierFormDataSource}>
      <FormListBaseGrid dataSource={gibierFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) setMode(Mode.Lecture);
        }}
      >
        <Column key={"grid-col-nom"} dataField={"nom"} caption="Nom" allowHiding={false} />
        <Column key={"grid-col-nom-anglais"} dataField={"nomAnglais"} caption="Nom anglais" allowHiding={false} />
        <Column key={"grid-col-type-de-gibier"} dataField={"typeDeGibier"} caption="Type de gibier" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedGibier?.nom || ''}`} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <TabItem title="Informations">
          <Form key={formRenderKey} ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedGibier || {} as IGibier} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onGibierFieldDataChanged}>
            <GroupItem>
              <GroupItem colCount={2} cssClass='noflexitemgroup'>
                <SimpleItem cssClass='noflexitem' key={"frm-nom"} dataField={"nom"} editorOptions={nomOptions} />
                <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"}
                  editorOptions={actifOptions}
                >
                  <Label visible={false} />
                </SimpleItem>
              </GroupItem>
              <SimpleItem key={"frm-nom-anglais"} dataField={"nomAnglais"}
                editorOptions={nomAnglaisOptions}>
                <Label text={"Nom anglais"} />
              </SimpleItem>
              <SimpleItem key={"frm-type-de-gibier"} dataField={"typeDeGibier"} editorType={"dxSelectBox"} editorOptions={typesGibierEditorOptions}>
                <Label text={"Type de gibiers"} />
              </SimpleItem>
            </GroupItem>
            <GroupItem caption={"Gibier"}>
              {gibierNoOptions && <SimpleItem key={`frm-gibier-${renderedTypeDeGibier}`} dataField={"noGibier"} editorType={"dxRadioGroup"}
                editorOptions={gibierNoOptions}>
                <Label visible={false} />
              </SimpleItem>
              }
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedGibier || {} as IGibier} /></TabItem>
      </FormListBaseTab>
    </FormListBase >
  );
}